import { useCapacitorStorage } from '@/composables/capacitor/useCapacitorStorage';
import { useCapacitorBiometry } from '@/composables/capacitor/useCapacitorBiometry';

export const PIN_STORAGE_KEY = 'appLockPin';
export const PIN_ENABLED_KEY = 'appLockPinEnabled';
export const BIOMETRY_ENABLED_KEY = 'biometryEnabled';

export const useAppLock = () => {
  const { getValue, setValue } = useCapacitorStorage();
  const { authenticate } = useCapacitorBiometry();

  const loadState = async () => {
    const [pinEnabled, biometryEnabled] = await Promise.all([
      getValue(PIN_ENABLED_KEY),
      getValue(BIOMETRY_ENABLED_KEY)
    ]);
    return {
      pinEnabled: pinEnabled === 'true',
      biometryEnabled: biometryEnabled === 'true'
    };
  };

  const enableBiometry = async () => {
    await authenticate();
    await Promise.all([
      setValue(BIOMETRY_ENABLED_KEY, 'true'),
      setValue(PIN_ENABLED_KEY, 'false'),
      setValue(PIN_STORAGE_KEY, undefined)
    ]);
  };

  const disableBiometry = async () => {
    await authenticate();
    await setValue(BIOMETRY_ENABLED_KEY, 'false');
  };

  const setPin = async (pin: string) => {
    await Promise.all([setValue(PIN_STORAGE_KEY, pin, true), setValue(PIN_ENABLED_KEY, 'true')]);
  };

  const removePin = async () => {
    await Promise.all([setValue(PIN_STORAGE_KEY, undefined), setValue(PIN_ENABLED_KEY, 'false')]);
  };

  const verifyPin = async (pin: string): Promise<boolean> => {
    const stored = await getValue(PIN_STORAGE_KEY);
    return stored === pin;
  };

  const unlockWithBiometry = async (): Promise<void> => {
    await authenticate();
  };

  const reset = async (): Promise<void> => {
    await Promise.all([
      setValue(BIOMETRY_ENABLED_KEY, 'false'),
      setValue(PIN_ENABLED_KEY, 'false'),
      setValue(PIN_STORAGE_KEY)
    ]);
  };

  return {
    loadState,
    enableBiometry,
    disableBiometry,
    setPin,
    removePin,
    verifyPin,
    unlockWithBiometry,
    reset
  };
};
